import axios, { AxiosError } from 'axios'
import { analyzeImage, analyzeImageByLink } from '@src/api/api'
import { FileSource, FileStatus } from '@src/components/FilePreview/types'
import { toast, ToastType } from '@src/components/ui/toaster'
import { MainPageStoreContext } from '@src/pages/MainPageStore'
import { useContext } from 'react'
import { FileItem } from '@src/components/FilePreviewList/FileItem'
import { downloadLink } from '@src/api/apiCloud'

export const useAnalyze = () => {
    const store = useContext(MainPageStoreContext)

    const worker = async () => {
        store.addActiveWorker()
        while (store.filesToRequest.length > 0) {
            const file = store.filesToRequest.shift()
            if (!file) {
                return
            }
            await analyze(file)
        }
        store.removeActiveWorker()
    }

    const analyze = async (file: FileItem) => {
        store.updateFileStatus(file.name, FileStatus.IN_PROGRESS)

        try {
            const controller = new AbortController()
            store.setAbortControllerToFile(file.name, controller)

            const { data } =
                file.sourceType === FileSource.FILE
                    ? await analyzeFromFile(file, controller.signal)
                    : await analyzeFromLink(file, controller.signal)

            store.setStatistics(data.objects)
            store.setChartsData(data.objects)
            store.setMapData(data.objects)

            store.addCompetedFile(file)

            store.updateFileStatus(file.name, FileStatus.COMPLETED)
            store.updateFileResult(file.name, data)
            toast(ToastType.SUCCESS, file.name)
        } catch (error) {
            if (axios.isCancel(error)) {
                store.updateFileStatus(file.name, FileStatus.CANCELED)
                return
            }
            console.log(error)

            store.updateFileStatus(file.name, FileStatus.ERROR)
            store.updateFileError(file.name, error as AxiosError)
            toast(ToastType.ERROR, file.name, (error as AxiosError).message)
        }
    }

    const analyzeFromFile = async (file: FileItem, signal: AbortSignal) => {
        const formData = new FormData()
        formData.append('file', file.binaryFile!)
        return analyzeImage(formData, signal)
    }

    const analyzeFromLink = async (file: FileItem, signal: AbortSignal) => {
        if (!file.infoFromCloud) {
            return Promise.reject()
        }

        const path = file.infoFromCloud.path!
        const public_key = file.infoFromCloud.public_key!

        const {
            data: { href },
        } = await downloadLink(public_key, path)

        return analyzeImageByLink(href, file.name, signal)
    }

    return { worker, analyze }
}
